'use client';

import Link from 'next/link';
import { MessageSquare, Clock, Bug, Lightbulb, HelpCircle, Megaphone } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ── Types ─────────────────────────────────────────────────────────────── */

export interface Discussion {
  id: string;
  title: string;
  body: string;
  category: string;
  status: string;
  author_name: string;
  author_business: string | null;
  comment_count: number;
  created_at: string;
}

/* ── Category / Status badges ──────────────────────────────────────────── */

const categoryConfig: Record<string, { label: string; icon: typeof Bug; color: string }> = {
  bug: { label: 'Bug Report', icon: Bug, color: 'var(--color-legacy-graphic-red)' },
  feature: { label: 'Feature Request', icon: Lightbulb, color: 'var(--color-legacy-graphic-amber)' },
  general: { label: 'General', icon: Megaphone, color: 'var(--color-legacy-graphic-indigo)' },
  question: { label: 'Question', icon: HelpCircle, color: 'var(--color-legacy-graphic-cyan)' },
};

const statusConfig: Record<string, { label: string; dot: string }> = {
  open: { label: 'Open', dot: 'var(--color-legacy-graphic-green-bright)' },
  in_progress: { label: 'In Progress', dot: 'var(--color-legacy-graphic-amber)' },
  solved: { label: 'Solved', dot: 'var(--color-legacy-graphic-indigo)' },
};

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

/* ── Card component ────────────────────────────────────────────────────── */

export default function DiscussionCard({ discussion }: { discussion: Discussion }) {
  const cat = categoryConfig[discussion.category] ?? categoryConfig.general;
  const status = statusConfig[discussion.status] ?? statusConfig.open;
  const CatIcon = cat.icon;

  return (
    <Link
      href={`/feedback/${discussion.id}`}
      className="group block rounded-[var(--pub-radius-xl)] p-5 transition-all hover:shadow-md"
      style={{
        background: 'var(--pub-surface-card)',
        border: '1px solid var(--pub-border-subtle)',
      }}
    >
      {/* Top row: category + status */}
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
          style={{
            background: `${cat.color}12`,
            color: cat.color,
          }}
        >
          <CatIcon className="h-3 w-3" />
          {cat.label}
        </span>
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
          style={{
            background: 'var(--pub-surface-base)',
            color: 'var(--pub-text-secondary)',
          }}
        >
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: status.dot }}
          />
          {status.label}
        </span>
      </div>

      {/* Title */}
      <h3
        className="mt-3 text-[15px] font-semibold leading-snug transition-colors group-hover:text-[var(--pub-ember)]"
        style={{ color: 'var(--pub-text-primary)' }}
      >
        {discussion.title}
      </h3>

      {/* Excerpt */}
      <p
        className="mt-2 line-clamp-2 text-[13.5px] leading-relaxed"
        style={{ color: 'var(--pub-text-secondary)' }}
      >
        {discussion.body}
      </p>

      {/* Footer: author + meta */}
      <div
        className="mt-4 flex items-center gap-3 text-xs"
        style={{ color: 'var(--pub-text-muted)' }}
      >
        <span className="font-medium" style={{ color: 'var(--pub-text-secondary)' }}>
          {discussion.author_name}
        </span>
        {discussion.author_business && (
          <>
            <span style={{ color: 'var(--pub-border-default)' }}>·</span>
            <span>{discussion.author_business}</span>
          </>
        )}
        <span style={{ color: 'var(--pub-border-default)' }}>·</span>
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {timeAgo(discussion.created_at)}
        </span>
        <span style={{ color: 'var(--pub-border-default)' }}>·</span>
        <span className="inline-flex items-center gap-1">
          <MessageSquare className="h-3 w-3" />
          {discussion.comment_count}
        </span>
      </div>
    </Link>
  );
}
